/**
 * The clinic's front page: the copy shown on `/home` when the hostname names a clinic, and
 * edited by that clinic's administrator in the console.
 *
 * Three bands, which is exactly what the page draws:
 *
 *   hero      (eyebrow + a two-line headline + subtitle, over an optional photograph)
 *   features  (eyebrow + heading + standfirst, over a band of cards)
 *   call to action (heading + subtitle)
 *
 * <h2>What is stored is what is published</h2>
 *
 * Every clinic's page is stored whole: the platform writes the starter copy into it, in both
 * languages, when the clinic is created, and the clinic rewrites it from there. So the editor
 * opens on the page exactly as patients read it, and a blank field means **that line is not on
 * the page** -- `homeContent.ts` resolves it to nothing and the view leaves it out.
 *
 * Only the first line of the Romanian headline is required, by the form and the API alike.
 *
 * Every label carries both languages. Romanian is what the clinic writes; English is optional
 * and falls back to Romanian.
 */

import { newKey } from './localisedText';

/**
 * What a new card starts with. Lives here rather than in the icon module so the model does
 * not depend on one that returns JSX; that module imports this one.
 */
export const DEFAULT_HOME_CARD_ICON: string = 'HEALING';

/** What the API returns, from `GET /public/tenant-home` or `GET /tenant-home`. */
export interface ITenantHome {
    heroEyebrowRo?: string | null;
    heroEyebrowEn?: string | null;
    heroHeadline1Ro?: string | null;
    heroHeadline1En?: string | null;
    heroHeadline2Ro?: string | null;
    heroHeadline2En?: string | null;
    heroSubtitleRo?: string | null;
    heroSubtitleEn?: string | null;
    featuresEyebrowRo?: string | null;
    featuresEyebrowEn?: string | null;
    featuresTitleRo?: string | null;
    featuresTitleEn?: string | null;
    featuresSubtitleRo?: string | null;
    featuresSubtitleEn?: string | null;
    ctaTitleRo?: string | null;
    ctaTitleEn?: string | null;
    ctaSubtitleRo?: string | null;
    ctaSubtitleEn?: string | null;
    /** Relative to the API, as the logo's is. Null when the clinic has uploaded no photograph. */
    heroImageUrl?: string | null;
    heroImageEtag?: string | null;
    cards: ITenantHomeCard[];
}

export interface ITenantHomeCard {
    /** The server's. Here for the page to key its list on, nothing more. */
    id: number;
    /** A name from the curated set in `homeIcons`. Null means the default one. */
    icon?: string | null;
    titleRo: string;
    titleEn?: string | null;
    bodyRo: string;
    bodyEn?: string | null;
}

/**
 * The draft the editor holds.
 *
 * Cards carry a `key` and no `id`, for the reason the profile's draft gives: the API replaces
 * a clinic's cards rather than reconciling them, so an id would be a number the editor carried
 * around and the server ignored.
 *
 * The hero photograph is not here. It is uploaded the moment it is cropped, through a route of
 * its own, so it is never part of a draft that can be discarded -- the same way the logo works
 * in the branding tab, and for the same reason: a multipart upload has no business inside a
 * JSON save.
 *
 * Nulls arrive as empty strings, because that is what a controlled text input holds.
 */
export interface HomeDraft {
    heroEyebrowRo: string;
    heroEyebrowEn: string;
    heroHeadline1Ro: string;
    heroHeadline1En: string;
    heroHeadline2Ro: string;
    heroHeadline2En: string;
    heroSubtitleRo: string;
    heroSubtitleEn: string;
    featuresEyebrowRo: string;
    featuresEyebrowEn: string;
    featuresTitleRo: string;
    featuresTitleEn: string;
    featuresSubtitleRo: string;
    featuresSubtitleEn: string;
    ctaTitleRo: string;
    ctaTitleEn: string;
    ctaSubtitleRo: string;
    ctaSubtitleEn: string;
    cards: HomeCardDraft[];
}

export interface HomeCardDraft {
    key: string;
    icon: string;
    titleRo: string;
    titleEn: string;
    bodyRo: string;
    bodyEn: string;
}

/** What `PUT /tenant-home` takes: the same shape, trimmed, with the editor's `key` dropped. */
export interface TenantHomeRequest {
    heroEyebrowRo: string;
    heroEyebrowEn: string;
    heroHeadline1Ro: string;
    heroHeadline1En: string;
    heroHeadline2Ro: string;
    heroHeadline2En: string;
    heroSubtitleRo: string;
    heroSubtitleEn: string;
    featuresEyebrowRo: string;
    featuresEyebrowEn: string;
    featuresTitleRo: string;
    featuresTitleEn: string;
    featuresSubtitleRo: string;
    featuresSubtitleEn: string;
    ctaTitleRo: string;
    ctaTitleEn: string;
    ctaSubtitleRo: string;
    ctaSubtitleEn: string;
    cards: HomeCardRequest[];
}

export interface HomeCardRequest {
    icon: string;
    titleRo: string;
    titleEn: string;
    bodyRo: string;
    bodyEn: string;
}

/**
 * A new card starts with an icon already chosen rather than none, so the tile it draws is
 * never an empty square the admin has to notice and go fix.
 */
export const emptyHomeCard = (): HomeCardDraft => ({
    key: newKey(),
    icon: DEFAULT_HOME_CARD_ICON,
    titleRo: '',
    titleEn: '',
    bodyRo: '',
    bodyEn: '',
});

/** Opens the editor on what is currently stored -- which may be nothing at all. */
export const toHomeDraft = (home: ITenantHome | undefined): HomeDraft => ({
    heroEyebrowRo: home?.heroEyebrowRo ?? '',
    heroEyebrowEn: home?.heroEyebrowEn ?? '',
    heroHeadline1Ro: home?.heroHeadline1Ro ?? '',
    heroHeadline1En: home?.heroHeadline1En ?? '',
    heroHeadline2Ro: home?.heroHeadline2Ro ?? '',
    heroHeadline2En: home?.heroHeadline2En ?? '',
    heroSubtitleRo: home?.heroSubtitleRo ?? '',
    heroSubtitleEn: home?.heroSubtitleEn ?? '',
    featuresEyebrowRo: home?.featuresEyebrowRo ?? '',
    featuresEyebrowEn: home?.featuresEyebrowEn ?? '',
    featuresTitleRo: home?.featuresTitleRo ?? '',
    featuresTitleEn: home?.featuresTitleEn ?? '',
    featuresSubtitleRo: home?.featuresSubtitleRo ?? '',
    featuresSubtitleEn: home?.featuresSubtitleEn ?? '',
    ctaTitleRo: home?.ctaTitleRo ?? '',
    ctaTitleEn: home?.ctaTitleEn ?? '',
    ctaSubtitleRo: home?.ctaSubtitleRo ?? '',
    ctaSubtitleEn: home?.ctaSubtitleEn ?? '',
    cards: (home?.cards ?? []).map(toCardDraft),
});

const toCardDraft = (card: ITenantHomeCard): HomeCardDraft => ({
    key: newKey(),
    icon: card.icon ?? '',
    titleRo: card.titleRo,
    titleEn: card.titleEn ?? '',
    bodyRo: card.bodyRo,
    bodyEn: card.bodyEn ?? '',
});

/**
 * The draft as the API takes it.
 *
 * Blank fields are sent as blank rather than omitted, and that is load-bearing: the save is a
 * full replace, and a blank field is how an admin takes one line off the page.
 */
export const toTenantHomeRequest = (draft: HomeDraft): TenantHomeRequest => ({
    heroEyebrowRo: draft.heroEyebrowRo.trim(),
    heroEyebrowEn: draft.heroEyebrowEn.trim(),
    heroHeadline1Ro: draft.heroHeadline1Ro.trim(),
    heroHeadline1En: draft.heroHeadline1En.trim(),
    heroHeadline2Ro: draft.heroHeadline2Ro.trim(),
    heroHeadline2En: draft.heroHeadline2En.trim(),
    heroSubtitleRo: draft.heroSubtitleRo.trim(),
    heroSubtitleEn: draft.heroSubtitleEn.trim(),
    featuresEyebrowRo: draft.featuresEyebrowRo.trim(),
    featuresEyebrowEn: draft.featuresEyebrowEn.trim(),
    featuresTitleRo: draft.featuresTitleRo.trim(),
    featuresTitleEn: draft.featuresTitleEn.trim(),
    featuresSubtitleRo: draft.featuresSubtitleRo.trim(),
    featuresSubtitleEn: draft.featuresSubtitleEn.trim(),
    ctaTitleRo: draft.ctaTitleRo.trim(),
    ctaTitleEn: draft.ctaTitleEn.trim(),
    ctaSubtitleRo: draft.ctaSubtitleRo.trim(),
    ctaSubtitleEn: draft.ctaSubtitleEn.trim(),
    cards: draft.cards.map(toCardRequest),
});

const toCardRequest = (card: HomeCardDraft): HomeCardRequest => ({
    icon: card.icon.trim(),
    titleRo: card.titleRo.trim(),
    titleEn: card.titleEn.trim(),
    bodyRo: card.bodyRo.trim(),
    bodyEn: card.bodyEn.trim(),
});

/**
 * The draft as the front page would receive it, for the console's live preview.
 *
 * The preview renders through exactly the resolver the page uses, so the draft has to arrive
 * in the shape the API answers with. Empty strings need no converting to nulls: the resolver
 * trims and measures rather than testing for null, precisely so that a half-typed field and an
 * unwritten one behave the same on screen.
 *
 * Card ids are positions, which is honest -- a draft card has no id until it is saved, and the
 * resolver only uses the id to key a list.
 */
export const toPreviewHome = (
    draft: HomeDraft,
    heroImageUrl: string | null,
    heroImageEtag: string | null
): ITenantHome => ({
    ...draft,
    heroImageUrl,
    heroImageEtag,
    cards: draft.cards.map((card: HomeCardDraft, index: number) => ({
        id: index,
        icon: card.icon,
        titleRo: card.titleRo,
        titleEn: card.titleEn,
        bodyRo: card.bodyRo,
        bodyEn: card.bodyEn,
    })),
});
